import React, { useContext, useMemo, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Outlet, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
    AppBar, Avatar, Box, Button, Chip, Divider, Drawer, IconButton, List,
    ListItemButton, ListItemIcon, ListItemText, Stack, Toolbar, Tooltip,
    Typography, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import { ColorModeContext } from '../theme/SmartPayThemeProvider';
import smartPayMark from '../assets/brand/smartpay-mark.svg';
import SmartPayIcon from '../components/ui/SmartPayIcon';

const expandedWidth = 284;
const collapsedWidth = 88;

const navigation = [
    { label: 'Resumen', items: [{ text: 'Inicio', path: 'overview', icon: 'home', admin: false }] },
    { label: 'Negocio', items: [
        { text: 'Afiliaciones', path: 'affiliations', icon: 'affiliations', admin: false },
        { text: 'Comercios', path: 'merchants', icon: 'merchants', admin: false },
        { text: 'Terminales', path: 'terminals', icon: 'terminals', admin: false },
    ] },
    { label: 'Configuración', items: [
        { text: 'Modelos de terminal', path: 'terminal-models', icon: 'models', admin: true },
        { text: 'Operativas', path: 'operational-configuration', icon: 'settings', admin: true },
    ] },
    { label: 'Control', items: [
        { text: 'Monitoreo', path: 'copayment-logs', icon: 'monitoring', admin: true },
        { text: 'Usuarios', path: 'users', icon: 'users', admin: true },
    ] },
];

const Dashboard = () => {
    const { auth, logout } = useContext(AuthContext);
    const { mode, toggleColorMode } = useContext(ColorModeContext);
    const theme = useTheme();
    const desktop = useMediaQuery(theme.breakpoints.up('lg'));
    const location = useLocation();
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false);
    const drawerWidth = expanded ? expandedWidth : collapsedWidth;

    const visibleNavigation = useMemo(() => navigation.map((group) => ({
        ...group,
        items: group.items.filter((item) => !item.admin || auth?.role === 'admin')
    })).filter((group) => group.items.length > 0), [auth?.role]);
    const activeItem = visibleNavigation.flatMap((group) => group.items)
        .find((item) => location.pathname.includes(`/dashboard/${item.path}`));

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const drawerContent = (
        <Stack sx={{ height: '100%', color: '#DCE8EF' }}>
            <Stack direction="row" alignItems="center" spacing={1.4} sx={{ height: 78, px: expanded ? 2.5 : 2, flexShrink: 0 }}>
                <Box component="img" src={smartPayMark} alt="SmartPay" sx={{ width: 44, height: 44, display: 'block', flexShrink: 0 }} />
                {expanded && <Box>
                    <Typography sx={{ fontSize: 24, lineHeight: 1, fontWeight: 820, letterSpacing: '-.055em' }}><Box component="span" sx={{ color: '#B8AFFB' }}>smart</Box><Box component="span" sx={{ color: '#52E4D6' }}>pay</Box></Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(220,232,239,.58)', letterSpacing: '.12em' }}>PAYMENTS OS</Typography>
                </Box>}
            </Stack>
            <Divider sx={{ borderColor: 'rgba(255,255,255,.08)' }} />

            <Box sx={{ flex: 1, overflowY: 'auto', px: 1.3, py: 2 }}>
                {visibleNavigation.map((group) => (
                    <Box key={group.label} sx={{ mb: 2 }}>
                        {expanded && <Typography variant="overline" sx={{ display: 'block', px: 1.5, mb: .5, color: 'rgba(220,232,239,.42)', fontWeight: 750, letterSpacing: '.12em' }}>{group.label}</Typography>}
                        <List disablePadding>
                            {group.items.map((item) => {
                                const selected = location.pathname.includes(`/dashboard/${item.path}`);
                                return (
                                    <Tooltip key={item.path} title={expanded ? '' : item.text} placement="right">
                                        <ListItemButton component={RouterLink} to={item.path} selected={selected} onClick={() => setMobileOpen(false)} sx={{
                                            minHeight: 52, mb: .65, borderRadius: 3, px: expanded ? 1.1 : 2,
                                            justifyContent: expanded ? 'initial' : 'center', color: selected ? '#FFFFFF' : 'rgba(220,232,239,.72)',
                                            '&.Mui-selected': { bgcolor: 'rgba(103,83,231,.18)', color: '#FFFFFF', '&:hover': { bgcolor: 'rgba(103,83,231,.24)' } },
                                            '&:hover': { bgcolor: 'rgba(255,255,255,.06)', color: '#FFFFFF' }
                                        }}>
                                            <ListItemIcon sx={{ minWidth: expanded ? 50 : 0, justifyContent: 'center' }}><SmartPayIcon name={item.icon} size={38} sx={{ boxShadow: selected ? '0 8px 20px rgba(0,0,0,.30)' : '0 6px 14px rgba(0,0,0,.18)' }} /></ListItemIcon>
                                            {expanded && <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 14, fontWeight: selected ? 700 : 560 }} />}
                                            {expanded && selected && <KeyboardArrowRightRoundedIcon fontSize="small" />}
                                        </ListItemButton>
                                    </Tooltip>
                                );
                            })}
                        </List>
                    </Box>
                ))}
            </Box>

            <Box sx={{ p: 1.5 }}>
                <Box sx={{ p: expanded ? 1.5 : 1, borderRadius: 3, bgcolor: 'rgba(255,255,255,.055)', border: '1px solid rgba(255,255,255,.07)' }}>
                    <Stack direction="row" alignItems="center" spacing={1.2} justifyContent={expanded ? 'flex-start' : 'center'}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'secondary.main', fontWeight: 750 }}>{(auth?.username || 'S').charAt(0).toUpperCase()}</Avatar>
                        {expanded && <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" noWrap sx={{ color: '#FFF', fontWeight: 680 }}>{auth?.username || 'Sesión activa'}</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(220,232,239,.55)' }}>{auth?.role === 'admin' ? 'Administrador' : 'Operador'}</Typography>
                        </Box>}
                    </Stack>
                    {expanded && <Button fullWidth size="small" startIcon={<LogoutRoundedIcon />} onClick={handleLogout} sx={{ mt: 1.2, color: 'rgba(255,255,255,.72)', justifyContent: 'flex-start' }}>Cerrar sesión</Button>}
                </Box>
            </Box>
        </Stack>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <Drawer variant={desktop ? 'permanent' : 'temporary'} open={desktop || mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }} sx={{
                width: desktop ? drawerWidth : 0, flexShrink: 0,
                '& .MuiDrawer-paper': { width: drawerWidth, border: 0, overflowX: 'hidden', transition: 'width .22s ease', background: mode === 'light' ? 'linear-gradient(180deg, #172D43 0%, #0E2034 100%)' : 'linear-gradient(180deg, #0E2034 0%, #091827 100%)' }
            }}>
                {drawerContent}
            </Drawer>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <AppBar position="sticky" elevation={0} color="transparent" sx={{ backdropFilter: 'blur(16px)', bgcolor: mode === 'light' ? 'rgba(245,247,251,.84)' : 'rgba(11,24,40,.84)', borderBottom: 1, borderColor: 'divider' }}>
                    <Toolbar sx={{ minHeight: '72px !important', px: { xs: 2, md: 3.5 } }}>
                        <IconButton onClick={() => desktop ? setExpanded((value) => !value) : setMobileOpen(true)} sx={{ mr: 1.5 }} aria-label="Alternar navegación">
                            {desktop && expanded ? <MenuOpenRoundedIcon /> : <MenuRoundedIcon />}
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary">SmartPay / {activeItem?.text || 'Inicio'}</Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 720, lineHeight: 1.25 }}>{activeItem?.text || 'Centro de operación'}</Typography>
                        </Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Chip size="small" label="Desarrollo" color="secondary" variant="outlined" sx={{ display: { xs: 'none', sm: 'flex' } }} />
                            <Tooltip title={mode === 'light' ? 'Usar modo oscuro' : 'Usar modo claro'}>
                                <IconButton onClick={toggleColorMode}>{mode === 'light' ? <DarkModeRoundedIcon /> : <LightModeRoundedIcon />}</IconButton>
                            </Tooltip>
                            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14 }}>{(auth?.username || 'S').charAt(0).toUpperCase()}</Avatar>
                        </Stack>
                    </Toolbar>
                </AppBar>

                <Box component="main" sx={{ p: { xs: 2, sm: 2.5, md: 3.5 }, maxWidth: 1680, mx: 'auto' }}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
};

export default Dashboard;
